import { useCallback, useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { fetchWithRetry } from '@/lib/apiRequest';

export interface LegalProduct {
  id: string;
  name: string;
  slug: string | null;
  category: string | null;
  description: string | null;
  targetAudience: string | null;
  baseFee: number | null;
  successFeePercentage: number | null;
  minFee: number | null;
  maxFee: number | null;
  installmentCount: number | null;
  isActive: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateLegalProductInput {
  name: string;
  slug?: string | null;
  category?: string | null;
  description?: string | null;
  targetAudience?: string | null;
  baseFee?: number | null;
  successFeePercentage?: number | null;
  minFee?: number | null;
  maxFee?: number | null;
  installmentCount?: number | null;
  isActive?: boolean;
  notes?: string | null;
}

export type UpdateLegalProductInput = CreateLegalProductInput;

type UseLegalProductsOptions = {
  autoLoad?: boolean;
};

const toNullableText = (value: unknown) => {
  if (value === undefined || value === null) {
    return null;
  }

  const text = String(value).trim();
  return text === '' ? null : text;
};

const toNullableNumber = (value: unknown) => {
  if (value === undefined || value === null || value === '') {
    return null;
  }

  const normalized = String(value).replace(',', '.');
  const number = Number(normalized);
  return Number.isFinite(number) ? number : null;
};

const toNullableInteger = (value: unknown) => {
  if (value === undefined || value === null || value === '') {
    return null;
  }

  const normalized = String(value).replace(',', '.');
  const number = Number(normalized);
  return Number.isInteger(number) ? number : null;
};

const toBoolean = (value: unknown) => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return value === 1;
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['0', 'false', 'inactive', 'inativo', 'nao', 'não'].includes(normalized)) {
      return false;
    }
    if (['1', 'true', 'active', 'ativo'].includes(normalized)) {
      return true;
    }
  }

  return Boolean(value);
};

const sortLegalProducts = (a: LegalProduct, b: LegalProduct) =>
  a.name.localeCompare(b.name, 'pt-BR');

const normalizeLegalProduct = (value: any): LegalProduct => ({
  id: String(value?.id ?? ''),
  name: String(value?.name ?? '').trim(),
  slug: toNullableText(value?.slug),
  category: toNullableText(value?.category),
  description: toNullableText(value?.description),
  targetAudience: toNullableText(value?.targetAudience ?? value?.target_audience),
  baseFee: toNullableNumber(value?.baseFee ?? value?.base_fee),
  successFeePercentage: toNullableNumber(value?.successFeePercentage ?? value?.success_fee_percentage),
  minFee: toNullableNumber(value?.minFee ?? value?.min_fee),
  maxFee: toNullableNumber(value?.maxFee ?? value?.max_fee),
  installmentCount: toNullableInteger(value?.installmentCount ?? value?.installment_count),
  isActive: toBoolean(value?.isActive ?? value?.is_active),
  notes: toNullableText(value?.notes),
  createdAt: toNullableText(value?.createdAt ?? value?.created_at) ?? new Date().toISOString(),
  updatedAt: toNullableText(value?.updatedAt ?? value?.updated_at) ?? new Date().toISOString(),
});

const sortAndSetLegalProducts = (
  setLegalProducts: Dispatch<SetStateAction<LegalProduct[]>>,
  next: LegalProduct[]
) => {
  setLegalProducts([...next].sort(sortLegalProducts));
};

export function useLegalProducts(options: UseLegalProductsOptions = {}) {
  const { autoLoad = true } = options;
  const [legalProducts, setLegalProducts] = useState<LegalProduct[]>([]);
  const [isLoadingLegalProducts, setIsLoadingLegalProducts] = useState(true);
  const [legalProductsError, setLegalProductsError] = useState<string | null>(null);

  const loadLegalProducts = useCallback(async () => {
    setIsLoadingLegalProducts(true);
    setLegalProductsError(null);

    try {
      const response = await fetchWithRetry('/api/legal-products');
      const data = await response.json();

      if (!response.ok || !data.success || !Array.isArray(data.legalProducts)) {
        throw new Error(data.error || `GET /api/legal-products falhou com status ${response.status}`);
      }

      const loaded = data.legalProducts.map(normalizeLegalProduct);
      sortAndSetLegalProducts(setLegalProducts, loaded);
    } catch (error) {
      console.error('Erro ao carregar produtos jurídicos reais. ', error);
      setLegalProducts([]);
      setLegalProductsError('Não foi possível carregar os dados reais. Tente novamente.');
    } finally {
      setIsLoadingLegalProducts(false);
    }
  }, []);

  const createLegalProduct = useCallback(async (input: CreateLegalProductInput) => {
    const response = await fetch('/api/legal-products', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input),
    });

    const data = await response.json();

    if (!response.ok || !data.success || !data.legalProduct) {
      throw new Error(data.error || `POST /api/legal-products falhou com status ${response.status}`);
    }

    const created = normalizeLegalProduct(data.legalProduct);
    setLegalProducts((prev) => [created, ...prev.filter((product) => product.id !== created.id)].sort(sortLegalProducts));
    return created;
  }, []);

  const updateLegalProduct = useCallback(async (id: string, input: UpdateLegalProductInput) => {
    const response = await fetch(`/api/legal-products/${encodeURIComponent(id)}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input),
    });

    const data = await response.json();

    if (!response.ok || !data.success || !data.legalProduct) {
      throw new Error(data.error || `PUT /api/legal-products/${id} falhou com status ${response.status}`);
    }

    const updated = normalizeLegalProduct(data.legalProduct);
    setLegalProducts((prev) => [updated, ...prev.filter((product) => product.id !== updated.id)].sort(sortLegalProducts));
    return updated;
  }, []);

  const deleteLegalProduct = useCallback(async (id: string) => {
    const response = await fetch(`/api/legal-products/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error || `DELETE /api/legal-products/${id} falhou com status ${response.status}`);
    }

    setLegalProducts((prev) => prev.filter((product) => product.id !== id));
  }, []);

  useEffect(() => {
    if (!autoLoad) {
      return;
    }

    void loadLegalProducts();
  }, [autoLoad, loadLegalProducts]);

  return {
    legalProducts,
    isLoadingLegalProducts,
    legalProductsError,
    loadLegalProducts,
    createLegalProduct,
    updateLegalProduct,
    deleteLegalProduct,
  };
}
